import { initializeApp } from 'firebase/app';
import {
  ReCaptchaEnterpriseProvider,
  getToken as getAppCheckTokenResult,
  initializeAppCheck,
} from 'firebase/app-check';
import {
  OAuthProvider,
  browserLocalPersistence,
  browserSessionPersistence,
  getAuth,
  inMemoryPersistence,
  onAuthStateChanged,
  sendPasswordResetEmail,
  setPersistence,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
} from 'firebase/auth';

/* Il provider OIDC registrato su Identity Platform per admin.justbit.it. Deve
   iniziare per `oidc.` ed essere identico da entrambe le parti. */
export const JUSTBIT_PROVIDER_ID = 'oidc.justbit';

export async function createFirebaseAuth(firebaseConfig, {
  appCheckSiteKey = '',
  justbitSsoEnabled = true,
} = {}) {
  const app = initializeApp(firebaseConfig);
  const auth = getAuth(app);
  try {
    await setPersistence(auth, browserLocalPersistence);
  } catch (_localPersistenceError) {
    try {
      await setPersistence(auth, browserSessionPersistence);
    } catch (_sessionPersistenceError) {
      await setPersistence(auth, inMemoryPersistence);
    }
  }

  const normalizedSiteKey = String(appCheckSiteKey || '').trim();
  const appCheck = normalizedSiteKey
    ? initializeAppCheck(app, {
        provider: new ReCaptchaEnterpriseProvider(normalizedSiteKey),
        isTokenAutoRefreshEnabled: true,
      })
    : null;

  const justbitProvider = justbitSsoEnabled ? new OAuthProvider(JUSTBIT_PROVIDER_ID) : null;

  return Object.freeze({
    auth,
    observe: (listener, onError) => onAuthStateChanged(auth, listener, onError),
    signInWithEmail: (email, password) => signInWithEmailAndPassword(auth, email, password),
    justbitSsoEnabled: Boolean(justbitProvider),
    signInWithJustbit: () => {
      if (!justbitProvider) throw new Error('L’accesso con Justbit non è configurato.');
      return signInWithPopup(auth, justbitProvider);
    },
    /* Recupero, non registrazione: resta self-service anche ora che gli account
       li crea soltanto un owner dal pannello Utenti. */
    sendPasswordReset: (email) => sendPasswordResetEmail(auth, email),
    signOut: () => signOut(auth),
    appCheckEnabled: Boolean(appCheck),
    getAppCheckToken: async (forceRefresh = false) => {
      if (!appCheck) return '';
      const result = await getAppCheckTokenResult(appCheck, forceRefresh);
      const token = String(result?.token || '');
      if (!token) throw new Error('Firebase App Check non ha restituito un token.');
      return token;
    },
  });
}
